import React, { useContext, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { NavigationBarIcon } from '@fuww/library/src/Navigation';
import Dropdown from '@fuww/library/src/DropdownMenu';
import UserIcon from '@fuww/library/src/Icons/User';
import { useRouter } from 'next/router';
import AccountBox from '@fuww/library/src/Icons/AccountBox';
import Logout from '@fuww/library/src/Icons/Logout';
import Login from '@fuww/library/src/Icons/Login';
import PersonAddAlt from '@fuww/library/src/Icons/PersonAddAlt';
import Cached from '@fuww/library/src/Icons/Cached';
import RocketLaunch from '@fuww/library/src/Icons/RocketLaunch';
import Skeleton from '@fuww/library/src/SkeletonLoader/Skeleton';
import {
  BadgeWrapper,
  UpdateBadge,
} from '@fuww/library/src/UpdateBadge';
import Anchor from '@fuww/library/src/Anchor';
import UserAvatar from '../Users/UserAvatar';
import LinkAndAnchor from '../LinkAndAnchor';
import { UserContext } from '../UserProvider';
import messages from '../../lib/messages.json';
import { saveRouterState } from '../../lib/routerState';
import clearSiteData from '../../lib/clearSiteData';

interface UpdateMessageEvent extends MessageEvent {
  data: {
    type: string;
  };
}

const UserTab = () => {
  const intl = useIntl();
  const router = useRouter();
  const { user, loading } = useContext(UserContext);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const handleServiceWorkerUpdate = (event: UpdateMessageEvent) => {
      if (event.data.type === 'UPDATE_AVAILABLE') {
        setUpdateAvailable(true);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener(
        'message',
        handleServiceWorkerUpdate,
      );
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener(
          'message',
          handleServiceWorkerUpdate,
        );
      }
    };
  }, []);

  const userItems = [
    <LinkAndAnchor
      route="profile"
      aria-label="Profile"
      fullWidth
    >
      <AccountBox fontSize="medium" />
      <span>
        {intl.formatMessage(messages.profile)}
      </span>
    </LinkAndAnchor>,
    <LinkAndAnchor
      route="logout"
      aria-label="logout"
      fullWidth
    >
      <Logout />
      <span>
        {intl.formatMessage(messages.logout)}
      </span>
    </LinkAndAnchor>,
  ];

  const guestItems = [
    <LinkAndAnchor
      route="login"
      aria-label="Login"
      onClick={() => {
        saveRouterState(router);
      }}
      fullWidth
    >
      <Login fontSize="medium" />
      <span>
        {intl.formatMessage(messages.login)}
      </span>
    </LinkAndAnchor>,
    <LinkAndAnchor
      route="register"
      aria-label="Create Account"
      fullWidth
    >
      <PersonAddAlt fontSize="medium" />
      <span>
        {intl.formatMessage(messages['register.createAccount'])}
      </span>
    </LinkAndAnchor>,
  ];

  const resetAndReloadItem = (
    <Anchor
      onClick={clearSiteData}
      href="/"
      fullWidth
      alert={updateAvailable}
    >
      {updateAvailable ? (
        <RocketLaunch fontSize="medium" />
      ) : (
        <Cached fontSize="medium" />
      )}
      <span>
        {updateAvailable
          ? intl.formatMessage(messages.getLatestVersion)
          : intl.formatMessage(messages.resetAndReload)}
      </span>
    </Anchor>
  );

  const menuItems = [...(user ? userItems : guestItems), resetAndReloadItem];

  return (
    <Dropdown
      top={(
        <NavigationBarIcon>
          <BadgeWrapper>
            { loading ? (
              <Skeleton variant="rectangular" width="24px">
                <UserIcon title="User" />
              </Skeleton>
            ) : (user ? (
              <UserAvatar
                size={24}
                user={user}
              />
            ) : (<UserIcon title="User" />))}
            {updateAvailable && <UpdateBadge />}
          </BadgeWrapper>
        </NavigationBarIcon>
      )}
      menuItems={menuItems}
    />
  );
};

export default UserTab;
